import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { Sparkles, Loader2, Upload, X } from "lucide-react";
import { aiAutofillErrorMessage } from "../lib/aiAutofillError";

const SECTORS = ["Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics", "E-commerce", "Manufacturing", "Edtech", "Hospitality"];
const COUNTRIES = ["Nigeria", "Kenya", "Ghana", "South Africa", "Egypt", "Rwanda", "Senegal", "Ethiopia", "Tanzania", "Uganda"];
const STAGES = ["Idea", "MVP", "Revenue", "Growth"];
const SDGS = ["SDG 2", "SDG 3", "SDG 4", "SDG 5", "SDG 7", "SDG 8", "SDG 9", "SDG 13"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const ipt = "w-full rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2.5 text-sm text-neutral-100 outline-none focus:border-[#F59E0B]";

export default function AdminBusinessNew() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    company_name: "", website: "", sector: "Fintech", country: "Nigeria", stage: "Revenue",
    ticket_min: 250000, ticket_max: 1000000, value_prop: "",
    revenue_annual: 0, employees: 1, sdgs: [],
    contact_name: "", contact_email: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // AI autofill state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiFile, setAiFile] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const [aiSugg, setAiSugg] = useState(null);

  const runAi = async () => {
    setAiBusy(true); setAiErr(""); setAiSugg(null);
    try {
      const fd = new FormData();
      if (aiText.trim()) fd.append("text", aiText.trim());
      if (aiFile) fd.append("file", aiFile);
      // 120s to cover the backend's ~90s worst-case budget + upload time
      // (matches the founder Onboarding pitch-upload timeout).
      const { data } = await api.post("/admin/snapshot/ai-suggest", fd,
        { timeout: 120000 });
      setAiSugg(data);
    } catch (e) {
      setAiErr(aiAutofillErrorMessage(e, { kind: "pitch" }));
    } finally { setAiBusy(false); }
  };

  const applyAi = () => {
    if (!aiSugg) return;
    setForm(s => ({ ...s,
      company_name: aiSugg.company_name || s.company_name,
      sector: aiSugg.sector || s.sector,
      country: aiSugg.country || s.country,
      stage: aiSugg.stage || s.stage,
      ticket_min: aiSugg.ticket_min ?? s.ticket_min,
      ticket_max: aiSugg.ticket_max ?? s.ticket_max,
      revenue_annual: aiSugg.revenue_annual ?? s.revenue_annual,
      employees: aiSugg.employees ?? s.employees,
      value_prop: aiSugg.value_prop || s.value_prop,
      sdgs: aiSugg.sdgs || s.sdgs,
    }));
    setAiOpen(false); setAiSugg(null); setAiText(""); setAiFile(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form,
        ticket_min: Number(form.ticket_min) || 0,
        ticket_max: Number(form.ticket_max) || 0,
        revenue_annual: Number(form.revenue_annual) || 0,
        employees: Number(form.employees) || 1,
      };
      const { data } = await api.post("/admin/businesses", payload);
      nav(`/admin/businesses/${data.company_id}`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create business");
    } finally { setBusy(false); }
  };

  const toggleSdg = (s) => setForm(f => ({ ...f, sdgs: f.sdgs.includes(s) ? f.sdgs.filter(x => x !== s) : [...f.sdgs, s] }));

  return (
    <AdminShell>
      <h1 className="text-3xl font-bold">New business</h1>
      <p className="mt-1 text-sm text-neutral-400">Create a managed SME on behalf of a founder.</p>

      <div className="mt-4 flex gap-2">
        <button type="button" onClick={() => setAiOpen(true)} data-testid="admin-ai-autofill-btn"
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-1.5 text-sm font-semibold text-[#F59E0B] hover:bg-[#F59E0B]/15">
          <Sparkles className="h-4 w-4"/> Auto-fill from pitch deck
        </button>
      </div>

      <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Company name">
          <input data-testid="admin-biz-name" required value={form.company_name}
            onChange={e => setForm({...form, company_name: e.target.value})} className={ipt}/>
        </Field>
        <Field label="Website">
          <input data-testid="admin-biz-website" value={form.website} placeholder="https://"
            onChange={e => setForm({...form, website: e.target.value})} className={ipt}/>
        </Field>
        <Field label="Sector">
          <select data-testid="admin-biz-sector" value={form.sector} onChange={e => setForm({...form, sector: e.target.value})} className={ipt}>
            {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Country">
          <select data-testid="admin-biz-country" value={form.country} onChange={e => setForm({...form, country: e.target.value})} className={ipt}>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Stage">
          <select data-testid="admin-biz-stage" value={form.stage} onChange={e => setForm({...form, stage: e.target.value})} className={ipt}>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Annual revenue (USD)">
          <input type="number" min="0" data-testid="admin-biz-revenue" value={form.revenue_annual}
            onChange={e => setForm({...form, revenue_annual: e.target.value})} className={ipt}/>
        </Field>
        <Field label="Ticket min (USD)">
          <input type="number" min="0" data-testid="admin-biz-ticket-min" value={form.ticket_min}
            onChange={e => setForm({...form, ticket_min: e.target.value})} className={ipt}/>
        </Field>
        <Field label="Ticket max (USD)">
          <input type="number" min="0" data-testid="admin-biz-ticket-max" value={form.ticket_max}
            onChange={e => setForm({...form, ticket_max: e.target.value})} className={ipt}/>
        </Field>
        <Field label="Employees">
          <input type="number" min="1" data-testid="admin-biz-employees" value={form.employees}
            onChange={e => setForm({...form, employees: e.target.value})} className={ipt}/>
        </Field>
        <div className="md:col-span-2">
          <Field label="Value proposition">
            <textarea data-testid="admin-biz-valueprop" required rows={3} value={form.value_prop}
              onChange={e => setForm({...form, value_prop: e.target.value})} className={ipt}/>
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="SDGs (alignment)">
            <div className="flex flex-wrap gap-2">
              {SDGS.map(s => (
                <button key={s} type="button" onClick={() => toggleSdg(s)} data-testid={`admin-biz-sdg-${s.replace(/\s+/g,'-')}`}
                  className={"rounded-full border px-3 py-1 text-xs font-semibold transition-colors " +
                    (form.sdgs.includes(s)
                      ? "border-[#F59E0B] bg-[#F59E0B]/20 text-[#F59E0B]"
                      : "border-neutral-800 bg-neutral-900 text-neutral-400 hover:border-neutral-700")}>
                  {s}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <div className="md:col-span-2 mt-4 rounded-xl border border-neutral-800 bg-neutral-900/50 p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Founder contact (optional)</p>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <input data-testid="admin-biz-contact-name" value={form.contact_name} placeholder="Contact name"
              onChange={e => setForm({...form, contact_name: e.target.value})} className={ipt}/>
            <input data-testid="admin-biz-contact-email" value={form.contact_email} placeholder="contact@business.co" type="email"
              onChange={e => setForm({...form, contact_email: e.target.value})} className={ipt}/>
          </div>
        </div>

        {err && <p className="md:col-span-2 text-sm text-red-400" data-testid="admin-biz-err">{String(err)}</p>}

        <div className="md:col-span-2 flex justify-end gap-2">
          <button type="button" onClick={() => nav("/admin/businesses")}
            className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-2 text-sm font-semibold text-neutral-300 hover:bg-neutral-800">
            Cancel
          </button>
          <button data-testid="admin-biz-submit" disabled={busy} type="submit"
            className="rounded-lg bg-[#F59E0B] px-5 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
            {busy ? "Creating…" : "Create business"}
          </button>
        </div>
      </form>

      {/* AI autofill modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="admin-ai-modal">
          <div className="w-full max-w-2xl rounded-2xl border border-neutral-800 bg-neutral-950 p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold">Auto-fill from pitch deck</h3>
                <p className="text-sm text-neutral-400">Upload a PDF or paste text. AI extracts the snapshot.</p>
              </div>
              <button onClick={() => setAiOpen(false)} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-800">
                <X className="h-5 w-5"/>
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-neutral-800 bg-neutral-900 px-4 py-6 text-sm text-neutral-400 hover:border-[#F59E0B]/50">
                <Upload className="h-5 w-5"/>
                <span>{aiFile ? aiFile.name : "Drop a PDF or click to upload"}</span>
                <input type="file" accept="application/pdf" data-testid="admin-ai-file" className="hidden"
                  onChange={e => setAiFile(e.target.files?.[0] || null)}/>
              </label>
              <textarea data-testid="admin-ai-text" placeholder="Or paste pitch / company description here…" rows={5}
                value={aiText} onChange={e => setAiText(e.target.value)} className={ipt}/>
              {aiErr && <p className="text-sm text-red-400">{aiErr}</p>}
              {!aiSugg && (
                <button onClick={runAi} disabled={aiBusy || (!aiText.trim() && !aiFile)} data-testid="admin-ai-run"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
                  {aiBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : <Sparkles className="h-4 w-4"/>}
                  {aiBusy ? "Extracting…" : "Extract snapshot"}
                </button>
              )}
              {aiSugg && (
                <div className="rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-4" data-testid="admin-ai-suggestion">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-[#F59E0B]">{aiSugg.company_name || "(no name)"}</p>
                    <span className="text-xs text-neutral-400">Confidence {aiSugg.confidence}%</span>
                  </div>
                  <p className="mt-1 text-xs text-neutral-300">{aiSugg.summary}</p>
                  <p className="mt-2 text-xs text-neutral-400">
                    {aiSugg.sector} · {aiSugg.country} · {aiSugg.stage} · ${aiSugg.ticket_min?.toLocaleString()}–${aiSugg.ticket_max?.toLocaleString()}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={applyAi} data-testid="admin-ai-apply"
                      className="rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308]">
                      Apply to form
                    </button>
                    <button onClick={() => { setAiSugg(null); }}
                      className="rounded-lg border border-neutral-800 px-4 py-2 text-sm font-semibold text-neutral-300 hover:bg-neutral-800">
                      Try again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
